import { useAccount } from "@apis/accounts/useAccounts"
import { accountKeys } from "@apis/accounts/queryKeys"
import { AccountContactPreferencesForm } from "@components/AccountNew/AccountContactPreferencesForm/AccountContactPreferencesForm"
import { AccountEmailForm } from "@components/AccountNew/AccountEmailForm/AccountEmailForm"
import { AccountPhoneNumberForm } from "@components/AccountNew/AccountPhoneNumberForm/AccountPhoneNumberForm"
import { AddressInfoForm } from "@components/Address/AddressInfoForm"
import type { TTitleValueItem } from "@components/TitleValueList/types"
import { Button } from "@components/ui/button"
import { useDrawer } from "@contexts/DrawerContext"
import { useQueryClient } from "@tanstack/react-query"
import { SquarePen } from "lucide-react"

type TAccountUtilityData = {
  items: TTitleValueItem[]
}

export const useAccountAboutSerializer = (account_id: string) => {
  const getAccountQuery = useAccount(account_id)
  const { closeDrawer, openDrawer } = useDrawer()
  const queryClient = useQueryClient()

  const data: TAccountUtilityData = {
    items: [
      {
        title: { content: "Service Address:" },
        value: {
          content: (() => {
            const serviceAddressLine1 =
              getAccountQuery.data?.service_address_line_1
            const serviceAddressLine2 =
              getAccountQuery.data?.service_address_line_2
            const serviceCity = getAccountQuery.data?.service_city
            const serviceState = getAccountQuery.data?.service_state
            const serviceZipCode = getAccountQuery.data?.service_zip_code

            const firstLine = [serviceAddressLine1, serviceAddressLine2]
              .filter(Boolean)
              .join(" ")
            const secondLine = [serviceCity, serviceState, serviceZipCode]
              .filter(Boolean)
              .join(", ")

            return (
              <Button
                variant="link"
                className="tw:h-fit tw:p-0 tw:text-left"
                onClick={() =>
                  openDrawer(
                    <AddressInfoForm
                      identifier={account_id}
                      address_guid={getAccountQuery.data?.service_address_guid}
                      address_type_code="service"
                      onSave={() => {
                        queryClient.invalidateQueries({
                          queryKey: accountKeys.detail(account_id),
                        })
                        closeDrawer()
                      }}
                      onCancel={() => closeDrawer()}
                      showMap
                    />,
                    "Update Profile Service Address",
                  )
                }
              >
                <span className="tw:flex tw:flex-col">
                  {firstLine && (
                    <span className="tw:text-pretty">{firstLine}</span>
                  )}
                  {secondLine && (
                    <span className="tw:text-pretty">{secondLine}</span>
                  )}
                  {!firstLine && !secondLine && <SquarePen size={16} />}
                </span>
              </Button>
            )
          })(),
          isPending: getAccountQuery.isPending,
        },
      },
      {
        title: { content: "Mailing Address:" },
        value: {
          content: (() => {
            const serviceAddressLine1 =
              getAccountQuery.data?.mailing_address_street
            const serviceAddressLine2 =
              getAccountQuery.data?.mailing_address_unit
            const serviceCity = getAccountQuery.data?.mailing_address_city
            const serviceState =
              getAccountQuery.data?.mailing_address_state_code
            const serviceZipCode = getAccountQuery.data?.mailing_address_zip

            const firstLine = [serviceAddressLine1, serviceAddressLine2]
              .filter(Boolean)
              .join(" ")
            const secondLine = [serviceCity, serviceState, serviceZipCode]
              .filter(Boolean)
              .join(", ")

            return (
              <Button
                variant="link"
                className="tw:h-fit tw:p-0 tw:text-left"
                onClick={() =>
                  openDrawer(
                    <AddressInfoForm
                      identifier={account_id}
                      address_guid={
                        getAccountQuery.data?.mailing_address_address_guid
                      }
                      address_type_code="billing"
                      onSave={() => {
                        queryClient.invalidateQueries({
                          queryKey: accountKeys.detail(account_id),
                        })
                        closeDrawer()
                      }}
                      onCancel={() => closeDrawer()}
                      showMap
                    />,
                    "Update Profile Mailing Address",
                  )
                }
              >
                <span className="tw:flex tw:flex-col">
                  {firstLine && (
                    <span className="tw:text-pretty">{firstLine}</span>
                  )}
                  {secondLine && (
                    <span className="tw:text-pretty">{secondLine}</span>
                  )}
                  {!firstLine && !secondLine && <SquarePen size={16} />}
                </span>
              </Button>
            )
          })(),
          isPending: getAccountQuery.isPending,
        },
      },
      {
        title: { content: "Email:" },
        value: {
          content: (() => {
            return (
              <Button
                variant="link"
                className="tw:items-start tw:p-0 tw:text-left"
                onClick={() =>
                  openDrawer(
                    <AccountEmailForm account_id={account_id} />,
                    "Update Profile Email",
                  )
                }
              >
                <span className="tw:flex tw:flex-col tw:text-pretty">
                  {getAccountQuery.data?.email}
                </span>
              </Button>
            )
          })(),
          isPending: getAccountQuery.isPending,
        },
      },
      {
        title: { content: "Phone:" },
        value: {
          content: (() => {
            if (!getAccountQuery.data) return null
            const primaryPhone = getAccountQuery.data.primary_phone
            const formattedPhone = primaryPhone
              ? primaryPhone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")
              : ""

            return (
              <Button
                variant="link"
                className="tw:items-start tw:p-0 tw:text-left"
                onClick={() =>
                  openDrawer(
                    <AccountPhoneNumberForm
                      prospect_guid={getAccountQuery.data.prospect_guid}
                      account_id={account_id}
                    />,
                    "Update Profile Phone Number",
                  )
                }
              >
                <span className="tw:flex tw:flex-col tw:text-pretty">
                  {formattedPhone}
                </span>
              </Button>
            )
          })(),
          isPending: getAccountQuery.isPending,
        },
      },
      {
        title: { content: "Speaks:" },
        value: {
          content:
            getAccountQuery.data?.membership_profile?.prospect
              ?.language_preference,
          isPending: getAccountQuery.isPending,
        },
      },
      {
        title: {
          content: (() => {
            return (
              <Button
                variant="link"
                className="tw:p-0"
                onClick={() =>
                  openDrawer(
                    <AccountContactPreferencesForm
                      prospect_guid={getAccountQuery.data?.prospect_guid}
                    />,
                    "Update Contact Preferences",
                  )
                }
              >
                <span>Contact Preferences</span>
              </Button>
            )
          })(),
        },
      },
    ],
  }

  return {
    data,
    isError: getAccountQuery.isError,
  }
}
